import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import dayjs from 'dayjs';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';

import Auth from 'src/services/api/auth/Auth';
import LandlordLayout from 'src/components/layout/landlord/LandlordLayout';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import AuthenticatedStateProvider from 'src/authenticated-state/AuthenticatedStateProvider';
import PaywallPage from 'src/pages/paywall/Paywall';
import AchievementNotification from 'src/components/AchievementNotification';
import { usePushNotifications } from 'src/services/push-notifications';
import { useHandleAchievements } from 'src/services/achievements/use-handle-achievements';

import 'src/components/layout/landlord/LandlordLayout.css';

const REFRESH_TOKEN_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

function AuthenticatedContainer() {
    const { loggedInUser, isPaywallEnabled, userSettings } = useAuthenticatedState();

    const { displayNotification, setDisplayNotification, lastEarnedAchievement } = useHandleAchievements();
    const { width, height } = useWindowSize();

    const [displayConfetti, setDisplayConfetti] = useState(false);
    const handleClose = () => setDisplayNotification(false);


    useEffect(() => {
        if (loggedInUser && !loggedInUser.is_onboarding_complete) {
            window.location.href = '/landlord/onboard';
        }
    }, [loggedInUser]);

    // Refresh the access token every 30 minutes
    useEffect(() => {
        const intervalId = window.setInterval(
            Auth.refreshAccessToken,
            REFRESH_TOKEN_INTERVAL_MS
        );

        return () => window.clearInterval(intervalId);
    }, []);

    // Refresh the access token when the user switches back to the tab
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                try {
                    const jwtPayload = Auth.jwtPayload;
                    const now = dayjs();
                    const issuedAt = dayjs.unix(jwtPayload?.iat || 0);

                    // If the token is older than 30 minutes, refresh it
                    if (now.diff(issuedAt, 'minute') > 30) {
                        Auth.refreshAccessToken();
                    }
                } catch (err) {
                    console.error(err);
                    Auth.logout();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        if (displayNotification) {
            setDisplayConfetti(true);
        } else {
            const timeoutId = setTimeout(() => {
                setDisplayConfetti(false);
            }, 3000);
            return () => clearTimeout(timeoutId);
        }
    }, [displayNotification]);

    usePushNotifications();

    if (isPaywallEnabled) {
        return (
            <PaywallPage user={loggedInUser} />
        );
    }

    const isAchievementsEnabled = userSettings.is_achievements_enabled;

    return (
        <LandlordLayout>
            <AchievementNotification
                isVisible={displayNotification && isAchievementsEnabled}
                achievementName={lastEarnedAchievement?.name || ''}
                achievementDetails={lastEarnedAchievement?.details || ''}
                onClose={handleClose}
            />

            {displayConfetti && isAchievementsEnabled && (
                <Confetti
                    width={width}
                    height={height}
                    recycle={false}
                    numberOfPieces={200}
                    gravity={0.3}
                    colors={['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7']}
                />
            )}

            <Outlet />
        </LandlordLayout>
    );
}

export default function AuthenticatedContainerWithProvider() {
    return (
        <AuthenticatedStateProvider>
            <AuthenticatedContainer />
        </AuthenticatedStateProvider>
    );
}
